<template>
  <el-dialog
    :title="dialogTitle"
    v-model="dialogVisible"
    width="800px"
    :before-close="handleClose"
    class="activity-dialog"
  >
    <el-form
      ref="activityForm"
      :model="formData"
      :rules="formRules"
      label-width="120px"
      size="default"
    >
      <!-- 活动标题 -->
      <el-form-item label="活动标题" prop="title">
        <el-input
          v-model="formData.title"
          placeholder="请输入活动标题"
          maxlength="50"
          show-word-limit
        />
      </el-form-item>

      <!-- 运动类型 -->
      <el-form-item label="运动类型" prop="sportType">
        <el-select
          v-model="formData.sportType"
          placeholder="请选择运动类型"
          style="width: 100%"
        >
          <el-option
            v-for="type in sportTypes"
            :key="type.value"
            :label="type.label"
            :value="type.value"
          />
        </el-select>
      </el-form-item>

      <!-- 活动地点 -->
      <el-form-item label="活动地点" prop="location">
        <el-input
          v-model="formData.location"
          placeholder="请输入活动地点"
          maxlength="100"
        />
      </el-form-item>

      <!-- 开始时间 -->
      <el-form-item label="开始时间" prop="startTime">
        <el-date-picker
          v-model="formData.startTime"
          type="datetime"
          placeholder="选择开始时间"
          style="width: 100%"
          :picker-options="startTimePickerOptions"
        />
      </el-form-item>

      <!-- 结束时间 -->
      <el-form-item label="结束时间" prop="endTime">
        <el-date-picker
          v-model="formData.endTime"
          type="datetime"
          placeholder="选择结束时间"
          style="width: 100%"
          :picker-options="endTimePickerOptions"
        />
      </el-form-item>

      <!-- 最大参与人数 -->
      <el-form-item label="最大参与人数" prop="maxParticipants">
        <el-input-number
          v-model="formData.maxParticipants"
          :min="1"
          :max="1000"
          placeholder="请输入最大参与人数"
          style="width: 100%"
        />
      </el-form-item>

      <!-- 报名费 -->
      <el-form-item label="报名费（元）" prop="fee">
        <el-input-number
          v-model="formData.fee"
          :min="0"
          :max="2000"
          :precision="2"
          placeholder="请输入报名费"
          style="width: 100%"
        />
      </el-form-item>

      <!-- 奖金金额 -->
      <el-form-item label="奖金金额（元）" prop="prizeAmount">
        <el-input-number
          v-model="formData.prizeAmount"
          :min="0"
          :max="10000"
          :precision="2"
          placeholder="请输入奖金金额"
          style="width: 100%"
        />
        <div class="form-tip">设置奖金将自动从您的钱包余额中扣除</div>
      </el-form-item>

      <!-- 奖金发放状态和手动触发 (仅编辑模式显示) -->
      <el-form-item label="奖金发放状态" v-if="mode === 'edit' && formData.prizeAmount > 0">
        <div class="prize-distribution-status">
          <div class="status-display">
            <el-tag 
              :type="prizeDistributionStatus.tagType" 
              effect="dark"
              size="large"
            >
              {{ prizeDistributionStatus.label }}
            </el-tag>
            <span class="status-detail" v-if="prizeDistributionStatus.detail">
              {{ prizeDistributionStatus.detail }}
            </span>
          </div>
          <el-button
            v-if="canManualDistribute"
            type="primary"
            size="small"
            :loading="distributingPrize"
            @click="handleManualDistributePrize"
          >
            <el-icon><Money /></el-icon>
            手动发放奖金
          </el-button>
          <el-button
            v-else-if="prizeDistributionStatus.status === 'pending'"
            type="info"
            size="small"
            :loading="checkingPrize"
            @click="handleRefreshPrizeStatus"
          >
            <el-icon><Refresh /></el-icon>
            刷新状态
          </el-button>
        </div>
        <div class="form-tip" v-if="prizeDistributionStatus.hint">
          {{ prizeDistributionStatus.hint }}
        </div>
      </el-form-item>

      <!-- 赛制选择 -->
      <el-form-item label="赛制" prop="tournamentFormat">
        <el-select
          v-model="formData.tournamentFormat"
          placeholder="请选择赛制"
          style="width: 100%"
        >
          <el-option label="无赛制" value="no_tournament" />
          <el-option label="单淘汰赛" value="single_elimination" />
          <el-option label="双淘汰赛" value="double_elimination" />
          <el-option label="小组赛+淘汰赛" value="group_stage_then_knockout" />
          <el-option label="循环赛" value="round_robin" />
        </el-select>
      </el-form-item>

      <!-- 活动描述 -->
      <el-form-item label="活动描述" prop="description">
        <el-input
          v-model="formData.description"
          type="textarea"
          :rows="4"
          placeholder="请输入活动描述"
          maxlength="500"
          show-word-limit
        />
      </el-form-item>

      <!-- 封面图片 -->
      <el-form-item label="封面图片">
        <el-upload
          class="avatar-uploader"
          action="/api/upload"
          :show-file-list="false"
          :on-success="handleAvatarSuccess"
          :before-upload="beforeAvatarUpload"
        >
          <img v-if="formData.cover_image" :src="formData.cover_image" class="avatar" />
          <i v-else class="el-icon-plus avatar-uploader-icon"></i>
        </el-upload>
        <div class="form-tip">建议尺寸：800x400px，支持JPG、PNG格式</div>
      </el-form-item>
    </el-form>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose" size="default">取消</el-button>
      <el-button
        type="primary"
        @click="handleSubmit"
        :loading="loading"
        size="default"
      >
        {{ submitButtonText }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import api from '@/utils/api'

export default {
  name: 'ActivityDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    activityData: {
      type: Object,
      default: () => null
    },
    mode: {
      type: String,
      default: 'create' // 'create' 或 'edit'
    }
  },
  emits: ['update:modelValue', 'success'],
  data() {
    return {
      dialogVisible: false,
      loading: false,
      distributingPrize: false,
      checkingPrize: false,
      prizeDistributionInfo: null,
      formData: {
        id: null,
        title: '',
        sportType: '',
        location: '',
        startTime: '',
        endTime: '',
        maxParticipants: null,
        fee: 0,
        prizeAmount: 0,
        tournamentFormat: 'no_tournament',
        description: '',
        cover_image: ''
      },
      sportTypes: [
        { label: '篮球', value: 'basketball' },
        { label: '足球', value: 'football' },
        { label: '羽毛球', value: 'badminton' },
        { label: '乒乓球', value: 'table_tennis' },
        { label: '网球', value: 'tennis' },
        { label: '排球', value: 'volleyball' },
        { label: '游泳', value: 'swimming' },
        { label: '跑步', value: 'running' },
        { label: '健身', value: 'fitness' },
        { label: '钓鱼', value: 'fishing' },
        { label: '其他', value: 'other' }
      ],
      formRules: {
        title: [
          { required: true, message: '请输入活动标题', trigger: 'blur' },
          { min: 2, max: 50, message: '标题长度在2到50个字符', trigger: 'blur' }
        ],
        sportType: [
          { required: true, message: '请选择运动类型', trigger: 'change' }
        ],
        location: [
          { required: true, message: '请输入活动地点', trigger: 'blur' },
          { min: 2, max: 100, message: '地点长度在2到100个字符', trigger: 'blur' }
        ],
        startTime: [
          { required: true, message: '请选择开始时间', trigger: 'change' }
        ],
        maxParticipants: [
          { required: true, message: '请输入最大参与人数', trigger: 'blur' }
        ],
        fee: [
          { validator: (rule, value, callback) => {
              const num = parseFloat(value)
              if (!value || value === 0 || isNaN(num)) { callback(); return; }
              if (num < 0) { callback(new Error('报名费不能为负数')); return; }
              if (num > 2000) { callback(new Error('报名费不能超过2000元')); return; }
              if (num > 0 && num < 1) { callback(new Error('报名费不能低于1元')); return; }
              if (Math.abs(Math.round(num * 100) - num * 100) > 1e-9) { callback(new Error('报名费最多保留两位小数')); return; }
              callback()
            }, trigger: 'blur' }
        ],
        prizeAmount: [
          { validator: (rule, value, callback) => {
              const num = parseFloat(value)
              if (!value || value === 0 || isNaN(num)) { callback(); return; }
              if (num < 0 || num > 10000) { callback(new Error('奖金额须为0或不低于1元（最高10000元）')); return; }
              if (num > 0 && num < 1) { callback(new Error('奖金额不能低于1元')); return; }
              if (Math.abs(Math.round(num * 100) - num * 100) > 1e-9) { callback(new Error('奖金额最多保留两位小数')); return; }
              callback()
            }, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    dialogTitle() {
      return this.mode === 'edit' ? '编辑活动' : '创建活动'
    },
    submitButtonText() {
      return this.loading ? '提交中...' : (this.mode === 'edit' ? '保存修改' : '创建活动')
    },
    // 奖金发放状态
    prizeDistributionStatus() {
      if (!this.prizeDistributionInfo) {
        return {
          status: 'pending',
          label: '等待发放',
          tagType: 'info',
          detail: '活动完成后将自动发放',
          hint: '如果活动已完成但未自动发放，可点击"手动发放奖金"按钮'
        }
      }

      const { status, distributed_at, recipient_name, error_message } = this.prizeDistributionInfo

      if (status === 'completed') {
        return {
          status: 'completed',
          label: '已发放',
          tagType: 'success',
          detail: `发放时间: ${this.formatDateTime(distributed_at)}${recipient_name ? ' | 获奖者: ' + recipient_name : ''}`,
          hint: null
        }
      } else if (status === 'failed') {
        return {
          status: 'failed',
          label: '发放失败',
          tagType: 'danger',
          detail: error_message || '发放过程出现错误',
          hint: '请检查错误信息后尝试手动发放'
        }
      } else {
        return {
          status: 'pending',
          label: '待发放',
          tagType: 'warning',
          detail: this.activityData?.status === 'completed' ? '活动已完成，等待发放' : '活动进行中',
          hint: this.activityData?.status === 'completed' ? '活动已完成但奖金未发放，请手动触发' : null
        }
      }
    },
    // 是否可以手动发放
    canManualDistribute() {
      if (!this.activityData) return false
      // 活动已完成、有奖金、未发放或发放失败的情况下可以手动发放
      return this.activityData.status === 'completed' 
        && this.formData.prizeAmount > 0
        && (!this.prizeDistributionInfo || this.prizeDistributionInfo.status !== 'completed')
    },
    startTimePickerOptions() {
      return {
        disabledDate: (time) => {
          return time.getTime() < Date.now() - 8.64e7 // 不能选择今天之前的日期
        }
      }
    },
    endTimePickerOptions() {
      return {
        disabledDate: (time) => {
          if (!this.formData.startTime) return time.getTime() < Date.now() - 8.64e7
          const startTime = new Date(this.formData.startTime)
          return time.getTime() < startTime.getTime()
        }
      }
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        this.dialogVisible = newVal
        if (newVal) {
          console.log('ActivityDialog打开，mode:', this.mode, 'activityData:', this.activityData)
          if (this.mode === 'edit' && this.activityData) {
            // 编辑模式：设置表单数据
            console.log('编辑模式，设置表单数据')
            console.log('原始activityData:', JSON.stringify(this.activityData, null, 2))
            
            // 字段映射：将后端字段名映射到前端表单字段名
            this.formData = { 
              id: this.activityData.id,
              title: this.activityData.title || '',
              sportType: this.activityData.sportType || '',
              location: this.activityData.location || '',
              startTime: this.activityData.startTime || '',
              endTime: this.activityData.endTime || '',
              // 参与人数字段映射 - 支持多种字段名
              maxParticipants: this.activityData.maxParticipants || this.activityData.participantCount || this.activityData.capacity || null,
              fee: this.activityData.fee || 0,
              // 奖金字段映射
              prizeAmount: this.activityData.prizeAmount || this.activityData.prize_amount || 0,
              tournamentFormat: this.activityData.tournamentFormat || this.activityData.tournament_format || 'no_tournament',
              description: this.activityData.description || '',
              cover_image: this.activityData.cover_image || this.activityData.coverImage || ''
            }
            
            console.log('映射后的formData:', JSON.stringify(this.formData, null, 2))
            
            // 转换时间格式
            if (this.formData.startTime) {
              this.formData.startTime = new Date(this.formData.startTime)
            }
            if (this.formData.endTime) {
              this.formData.endTime = new Date(this.formData.endTime)
            }
            
            console.log('时间转换后的formData:', this.formData)
            
            // 加载奖金发放状态
            if (this.formData.prizeAmount > 0) {
              this.loadPrizeDistributionStatus()
            }
          } else {
            // 创建模式：重置表单
            console.log('创建模式，重置表单')
            this.resetForm()
          }
        }
      }
    },
    dialogVisible(newVal) {
      this.$emit('update:modelValue', newVal)
    }
  },
  methods: {
    // 加载奖金发放状态
    async loadPrizeDistributionStatus() {
      if (!this.formData.id) return
      
      try {
        const response = await api.getPrizeDistributionStatus(this.formData.id)
        if (response && response.success) {
          this.prizeDistributionInfo = response.data
        }
      } catch (error) {
        console.error('加载奖金发放状态失败:', error)
      }
    },
    // 手动发放奖金
    async handleManualDistributePrize() {
      try {
        await this.$confirm(
          '确认要手动发放奖金吗？奖金将发放给活动获胜者。',
          '确认发放',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        this.distributingPrize = true
        const response = await api.manualDistributePrize(this.formData.id)
        
        if (response && response.success) {
          this.$message.success('奖金发放成功！')
          // 重新加载奖金发放状态
          await this.loadPrizeDistributionStatus()
        } else {
          this.$message.error(response.message || '奖金发放失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('手动发放奖金失败:', error)
          this.$message.error(error.response?.data?.message || '发放失败，请重试')
        }
      } finally {
        this.distributingPrize = false
      }
    },
    // 刷新奖金发放状态
    async handleRefreshPrizeStatus() {
      this.checkingPrize = true
      try {
        await this.loadPrizeDistributionStatus()
        this.$message.success('状态已刷新')
      } catch (error) {
        console.error('刷新状态失败:', error)
        this.$message.error('刷新失败，请重试')
      } finally {
        this.checkingPrize = false
      }
    },
    // 格式化日期时间
    formatDateTime(datetime) {
      if (!datetime) return ''
      const date = new Date(datetime)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },
    resetForm() {
      this.formData = {
        id: null,
        title: '',
        sportType: '',
        location: '',
        startTime: '',
        endTime: '',
        maxParticipants: null,
        fee: 0,
        prizeAmount: 0,
        tournamentFormat: 'no_tournament',
        description: '',
        cover_image: ''
      }
      if (this.$refs.activityForm) {
        this.$refs.activityForm.clearValidate()
      }
    },
    handleClose() {
      this.dialogVisible = false
      // 只在创建模式下重置表单，编辑模式下保留数据以便下次打开时显示
      if (this.mode === 'create') {
        this.resetForm()
      }
    },
    async handleSubmit() {
      try {
        const valid = await this.$refs.activityForm.validate()
        if (!valid) return

        this.loading = true
        
        // 准备提交数据 - 字段映射到后端字段名
        const submitData = {
          ...this.formData,
          // 前端字段名映射到后端字段名
          prize_amount: this.formData.prizeAmount || 0,
          participantCount: this.formData.maxParticipants || null,
          startTime: this.formData.startTime ? this.formData.startTime.toISOString() : '',
          endTime: this.formData.endTime ? this.formData.endTime.toISOString() : null
        }

        // 删除前端使用的字段名，避免重复
        delete submitData.prizeAmount
        delete submitData.maxParticipants

        let result
        if (this.mode === 'edit' && this.formData.id) {
          // 编辑活动
          result = await api.updateActivity(this.formData.id, submitData)
          this.$message.success('活动更新成功')
        } else {
          // 创建活动
          result = await api.createActivity(submitData)
          this.$message.success('活动创建成功')
        }

        this.$emit('success', result.data)
        this.handleClose()
      } catch (error) {
        console.error('提交活动失败:', error)
        this.$message.error(error.response?.data?.message || '操作失败，请重试')
      } finally {
        this.loading = false
      }
    },
    handleAvatarSuccess(res, file) {
      if (res.success && res.data) {
        this.formData.cover_image = res.data.url
        this.$message.success('图片上传成功')
      } else {
        this.$message.error('图片上传失败')
      }
    },
    beforeAvatarUpload(file) {
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/jpg'
      const isPNG = file.type === 'image/png'
      const isLt2M = file.size / 1024 / 1024 < 2

      if (!isJPG && !isPNG) {
        this.$message.error('上传图片只能是 JPG/PNG 格式!')
        return false
      }
      if (!isLt2M) {
        this.$message.error('上传图片大小不能超过 2MB!')
        return false
      }
      return true
    }
  }
}
</script>

<style scoped>
.activity-dialog {
  max-height: 80vh;
  overflow-y: auto;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

/* 奖金发放状态样式 */
.prize-distribution-status {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.status-display {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-detail {
  font-size: 13px;
  color: #606266;
}

.avatar-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 178px;
  height: 100px;
}

.avatar-uploader:hover {
  border-color: #409EFF;
}

.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 178px;
  height: 100px;
  line-height: 100px;
  text-align: center;
}

.avatar {
  width: 178px;
  height: 100px;
  display: block;
  object-fit: cover;
}

.dialog-footer {
  text-align: right;
  padding: 20px 0 0;
}
</style>